<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.loadingMessage"
    button-location="top"
    @submit="onSubmitForm"
  >
    <template #title>
      Create Saved Grocery
    </template>
    <template #content>
      <div>
        <el-steps
          :active="activeStep"
          finish-status="success"
          align-center
        >
          <el-step title="Saved Grocery Info" />
          <el-step title="Saved Grocery Store" />
          <el-step title="Saved Grocery Items" />
        </el-steps>
        <div class="text-center my-2 pb-1">
          <p-button
            v-show="activeStep !== 0"
            type="yellow"
            round
            outline
            @click="onBack()"
          >
            Back
          </p-button>
          <p-button
            type="yellow"
            round
            @click="reset()"
          >
            Reset
          </p-button>
          <p-button
            v-show="activeStep === 1"
            type="info"
            round
            @click="onSearchStores()"
          >
            Search Stores
          </p-button>
          <p-button
            v-show="activeStep === 2"
            type="info"
            round
            @click="status.isOpenSearchItem = true"
          >
            Search Items
          </p-button>
          <p-button
            type="purple"
            round
            native-type="submit"
          >
            {{ activeStep === 2 ? 'Create Saved Grocery' : 'Next' }}
          </p-button>
        </div>
        <div v-if="activeStep === 0">
          <div class="row align-items-center">
            <label class="col-sm-3 control-label">Caller Id*</label>
            <div class="col-sm-6">
              <sanitized-input
                id="callerId"
                v-model="callerId"
                placeholder="Please pull up account in right panel"
                required
              />
            </div>
          </div>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label">Saved Grocery Type</label>
            <div class="col-sm-6">
              <dropdown-list
                v-model="savedGroceryType"
                value-key="id"
                :options="savedGroceryTypeList"
              />
            </div>
          </div>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label">Saved Grocery Name*</label>
            <div class="col-sm-6">
              <sanitized-input
                id="savedGroceryName"
                v-model="savedGroceryName"
                placeholder="Please input saved grocery name"
                required
              />
            </div>
          </div>
          <div class="row">
            <label class="col-sm-3 control-label">Notes</label>
            <div class="col-sm-6">
              <sanitized-textarea
                id="savedGroceryNotes"
                v-model="savedGroceryNotes"
                placeholder="Please input saved grocery notes"
                autosize
              />
            </div>
          </div>
        </div>
        <div v-if="activeStep === 1">
          <div class="row align-items-center">
            <label class="col-sm-3 control-label">Grocery Address:</label>
            <div class="col-sm-6">
              <address-autocomplete-input
                id="address"
                v-model="fullAddress"
                address-type="Full Address"
                @place-changed="setAddressData"
              />
            </div>
          </div>
          <div
            v-if="selectedStore"
            class="row align-items-center"
          >
            <label class="col-sm-3 control-label">Selected Store:</label>
            <div class="col-sm-6 selected-store">
              <img :src="selectedStore.logo">
              <p>
                {{ selectedStore.name }}
              </p>
            </div>
          </div>
          <div />
          <div v-if="!selectedStore">
            <div class="row align-items-center">
              <div
                v-for="(groceryStore, index) in groceryStores"
                :key="index"
                class="col-lg-3 col-md-4 col-sm-6"
                @click="onSelectStore(groceryStore)"
              >
                <div class="store-item">
                  <img
                    class="store-logo"
                    :src="groceryStore.logo"
                  >
                  <div class="store-content">
                    <div class="store-name">
                      {{ groceryStore.name }}
                    </div>
                    <div class="store-description">
                      {{ groceryStore.description }}
                    </div>
                    <div class="service-badges-content">
                      <span
                        v-for="(badge, badgeIdx) in groceryStore.serviceTypeBadges"
                        :key="badgeIdx"
                        class="service-badge"
                        :style="`color: ${badge.color}; border: 1px solid ${badge.color};`"
                      >
                        {{ badge.label }}
                      </span>
                    </div>
                    <div class="status-badges-content">
                      <span
                        v-for="(badge, badgeIdx) in groceryStore.statusBadges"
                        :key="badgeIdx"
                        class="status-badge"
                        :style="`background-color: ${badge.color};`"
                      >
                        {{ badge.label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="activeStep === 2">
          <div class="row">
            <div class="col-sm-10 offset-sm-1 col-12">
              <saved-grocery-item
                v-for="(item, i) in storeItems"
                :key="i"
                :value="item"
                :store="selectedStore"
                :is-edit="true"
                :zip="selectedAddress.zip"
                :latitude="selectedAddress.latitude"
                :longitude="selectedAddress.longitude"
                :country="selectedAddress.country"
                @delete="onDeleteOrderItem(i)"
                @change="onChangeOrderItem($event, i)"
              />
            </div>
          </div>
        </div>
        <saved-grocery-search
          :is-show-dialog="status.isOpenSearchItem"
          :store="selectedStore"
          :zip="selectedAddress.zip"
          :latitude="selectedAddress.latitude"
          :longitude="selectedAddress.longitude"
          :country="selectedAddress.country"
          @select="onAddStoreItem"
          @cancel="status.isOpenSearchItem = false"
        />
      </div>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { reactive, Ref, ref, watch, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { CustomLocationType } from '@/models/address';
import { savedGroceryTypeList } from '@/constants/grocery';
import { groceryService } from '@/services/grocery';

const SavedGrocerySearch = defineAsyncComponent(() => import('./SavedGrocerySearch.vue') as any);

const SavedGroceryItem = defineAsyncComponent(() => import('./SavedGroceryItem.vue') as any);

const pageId = 'create-saved-grocery';

const router = useRouter();

const { customerInfo } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { getFormattedAddress } = useAddress();
const status = reactive({
  isLoading: false,
  loadingMessage: '',
  isOpenSearchItem: false,
});
const groceryStores: Ref<any> = ref([]);
const selectedAddress: Ref<any> = ref({
  streetAddress: '',
  streetAddress2: '',
  state: '',
  city: '',
  zip: '',
  country: '',
  latitude: null,
  longitude: null,
});
const fullAddress = ref('');
const selectedStore: Ref<any> = ref(null);
const savedGroceryName = ref('');
const savedGroceryType = ref(0);
const callerId = ref('');
const savedGroceryNotes = ref('');
const activeStep = ref(0);
const storeItems: Ref<any[]> = ref([]);

const createSavedGrocery = async () => {
  if (!storeItems.value.length) {
    showErrorPopupDialog({
      text: 'Please select one more items',
    });
    return;
  }

  const isConfirmed = await showConfirmPopupDialog({
    text: 'Are you sure you want to create this saved grocery?',
    title: 'Create Saved Grocery?',
    confirmButtonText: 'Create',
  });

  if (!isConfirmed) {
    return;
  }

  status.isLoading = true;
  status.loadingMessage = 'Creating saved grocery...';

  try {
    await groceryService.createSavedGrocery({
      type: savedGroceryType.value,
      name: savedGroceryName.value,
      notes: savedGroceryNotes.value,
      callerId: callerId.value,
      streetAddress: selectedAddress.value.streetAddress,
      streetAddress2: selectedAddress.value.streetAddress2 || null,
      city: selectedAddress.value.city,
      state: selectedAddress.value.state,
      zip: selectedAddress.value.zip,
      country: selectedAddress.value.country,
      latitude: selectedAddress.value.latitude,
      longitude: selectedAddress.value.longitude,
      store: {
        storeId: selectedStore.value.storeId,
        name: selectedStore.value.name,
        slug: selectedStore.value.slug,
        logo: selectedStore.value.logo,
      },
      storeItems: storeItems.value,
    });

    showSuccessPopupDialog({
      text: 'The saved grocery has been created successfully.',
    });

    router.push({ name: 'SavedGrocery' });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const onSearchStores = async () => {
  if (!selectedAddress.value.streetAddress) {
    showErrorPopupDialog({
      text: 'You must select an address from the dropdown',
    });
    return;
  }

  selectedStore.value = null;
  storeItems.value = [];

  try {
    status.isLoading = true;
    status.loadingMessage = 'Searching available stores';

    const stores = await groceryService.searchGroceryAvailableStoresAndSendEmail({
      streetAddress: selectedAddress.value.streetAddress!,
      city: selectedAddress.value.city!,
      state: selectedAddress.value.state!,
      zip: selectedAddress.value.zip!,
      latitude: selectedAddress.value.latitude!,
      longitude: selectedAddress.value.longitude!,
      country: selectedAddress.value.country!
    });

    if (!stores || !stores.length) {
      showErrorPopupDialog({
        text: 'Grocery service is not available in this area.',
      });
    }

    groceryStores.value = stores;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const onSubmitForm = () => {
  if (activeStep.value === 0) {
    activeStep.value = 1;
  } else if (activeStep.value === 1) {
    if (!selectedStore.value) {
      return showErrorPopupDialog({
        text: 'You must select a store',
      });
    }

    activeStep.value = 2;
  } else {
    createSavedGrocery();
  }
};

const setAddressData = (address: CustomLocationType | null) => {
  selectedStore.value = null;
  groceryStores.value = [];
  storeItems.value = [];
  selectedAddress.value = {
    streetAddress: address?.streetAddress,
    streetAddress2: address?.streetAddress2,
    state: address?.state,
    city: address?.city,
    zip: address?.zip,
    country: address?.country || '',
    latitude: address?.latitude ? Number(address.latitude) : null,
    longitude: address?.longitude ? Number(address.longitude) : null,
  };

  fullAddress.value = getFormattedAddress(selectedAddress.value);
};

const onSelectStore = (storeItem: any) => {
  storeItems.value = [];
  selectedStore.value = {
    storeId: storeItem.id.toString(),
    slug: storeItem.slug,
    name: storeItem.name,
    logo: storeItem.logo,
  };
};

const onAddStoreItem = (item: any) => {
  storeItems.value.push({
    qty: item.quantityAttributes.initial,
    groceryItem: {
      name: item.name,
      price: item.pricing.price,
      size: item.size,
      unit: item.unit,
      itemId: item.id,
      img: item.image,
      instacartProductId: item.productId,
      stockLevel: item.stockLevel,
    },
    replacementPolicy: 'no_replacements',
    instruction: 'If out of stock, refund the original item',
    replacementItem: null,
    replacementItemId: null,
    replacementItemQty: null,
  });

  status.isOpenSearchItem = false;
};

const onChangeOrderItem = (value: any, index: number) => {
  storeItems.value[index] = value;
};

const onDeleteOrderItem = (index: number) => {
  storeItems.value.splice(index, 1);
};

const onBack = () => {
  activeStep.value -= 1;
};

const reset = () => {
  selectedStore.value = null;
  groceryStores.value = [];
  storeItems.value = [];
  fullAddress.value = '';
  savedGroceryName.value = '';
  savedGroceryType.value = 0;
  selectedAddress.value = {
    streetAddress: '',
    streetAddress2: '',
    state: '',
    city: '',
    zip: '',
    country: '',
    latitude: null,
    longitude: null,
  };
  activeStep.value = 0;
};

watch(
  customerInfo,
  (value: any) => {
    if (value && value.id && callerId.value !== value?.id) {
      callerId.value = value.id;
    }
  },
  {
    immediate: true,
  },
);
</script>

<style lang="scss">
.create-saved-grocery {
  .store-item {
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 16px;
    flex-direction: column;
    align-items: center;
    border: none;
    background-color: white;
    border-radius: 10px;

    .store-logo {
      border-radius: 50%;
      box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 4px;
      background-color: white;
      width: 100px;
      height: 100px;
      margin-bottom: 14px;
    }
    .store-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      .store-name {
        margin-bottom: 4px;
        font-size: 19px;
        font-weight: 700;
        color: rgb(66, 66, 66);
      }
      .store-description {
        color: rgb(66, 66, 66);
        font-size: 15px;
      }
      .service-badges-content {
        .service-badge {
          height: 24px;
          line-height: 22px;
          padding-left: 8px;
          padding-right: 8px;
          box-sizing: border-box;
          display: inline-block;
          border-radius: 2px;
          font-size: 11px;
          font-weight: 700;
          margin-top: 10px;
          margin-left: 2px;
          margin-right: 2px;
          background-color: white;
          border: 1px solid rgb(238, 238, 238);
          color: rgb(117, 117, 117);
        }
      }
      .status-badges-content {
        position: absolute !important;
        top: 8px !important;
        right: 8px !important;

        .status-badge {
          height: 24px;
          line-height: 24px;
          padding-left: 8px;
          padding-right: 8px;
          box-sizing: border-box;
          display: inline-block;
          border-radius: 2px;
          font-size: 11px;
          font-weight: 700;
          color: white;
        }
      }
    }
  }
  .selected-store {
    display: flex;
    align-items: center;
    img {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      box-shadow: rgba(0, 0, 0, 0.15) 0px 2px 4px;
      background-color: white;
      margin-right: 10px;
    }
  }
}
</style>
